import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import QRCode from "qrcode";
import { toast } from "../hooks/useToast";
import { useStoreDialog } from "../hooks/useStoreDialog";
import { gerarPixPayload } from "../utils/pix";
import { fmtMoeda as fmt } from "../utils/format";
import { validarCarrinhoLoja, enviarPedidoLoja, mensagemPedidoLoja } from "../utils/pedidosLoja";
import { mpHabilitado, chavePublicaLoja, cancelarPagamentoLoja } from "../utils/mercadoPago";
import { clienteAuth } from "../utils/clienteAuth";
import { PagamentoMercadoPagoLoja } from "./loja/PagamentoMercadoPagoLoja";
import "./storeCart.css";

function novoRequestId() {
  return window.crypto?.randomUUID?.() || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, value => {
    const random = Math.floor(Math.random() * 16);
    return (value === "x" ? random : (random & 3) | 8).toString(16);
  });
}

/** Totals come from the server. Confirming payment does not mark an order as paid. */
export function CheckoutLoja({ vendedor, cliente, itens, subtotal, codigoCupom = "", onCancelar, onPedidoCriado }) {
  const cfg = useMemo(() => vendedor?.paymentConfig || {}, [vendedor]);
  const vendedorId = vendedor?.id;
  const [etapa, setEtapa] = useState("pagamento");
  const [metodo, setMetodo] = useState("pix");
  const [salvando, setSalvando] = useState(false);
  const [validando, setValidando] = useState(true);
  const [cotacao, setCotacao] = useState(null);
  const [erro, setErro] = useState("");
  const [tentativa, setTentativa] = useState(0);
  const [qrUrl, setQrUrl] = useState("");
  const dialogRef = useRef(null);
  const ocupado = useRef(false);
  const mounted = useRef(false);
  const requestId = useRef(null);
  if (!requestId.current) requestId.current = novoRequestId();
  const chaveItens = JSON.stringify(itens.map(({ id, qtd }) => ({ id, qtd })));
  // Mercado Pago da loja (em teste): chave pública do vendedor, se ele conectou
  const [mpChave, setMpChave] = useState(null);
  const [pedidoMp, setPedidoMp] = useState(null);
  useEffect(() => {
    if (!mpHabilitado() || !cliente?.uid || !vendedorId) return undefined;
    let atual = true;
    chavePublicaLoja(clienteAuth.app, vendedorId)
      .then(r => { if (atual) setMpChave(r?.publicKey || null); })
      .catch(() => { if (atual) setMpChave(null); });
    return () => { atual = false; };
  }, [cliente?.uid, vendedorId]);
  // Com Mercado Pago o pedido só segue para a loja se for pago. Fechar a janela
  // antes disso cancela (o servidor cancela o PIX pendente no Mercado Pago).
  const [pagandoMp, setPagandoMp] = useState(false);
  const [confirmarSaida, setConfirmarSaida] = useState(false);
  const [saindo, setSaindo] = useState(false);
  const concluido = useRef(false);
  const pagoMp = useCallback(() => {
    if (concluido.current || !pedidoMp) return;
    concluido.current = true;
    toast("Pagamento confirmado pelo Mercado Pago!", "success");
    onPedidoCriado(pedidoMp);
  }, [pedidoMp, onPedidoCriado]);
  const sairSemPagar = async () => {
    if (saindo || concluido.current) return;
    setSaindo(true);
    let situacao = "pendente";
    try { situacao = (await cancelarPagamentoLoja(clienteAuth.app, vendedorId, pedidoMp))?.situacao || "pendente"; }
    catch { situacao = "pendente"; }
    if (!mounted.current) return;
    if (situacao === "pago") { pagoMp(); return; }
    concluido.current = true;
    toast(situacao === "cancelado"
      ? "Pagamento cancelado. O pedido não foi enviado e seus itens continuam no carrinho."
      : "Pagamento não concluído. Se o Mercado Pago confirmar um pagamento, o pedido aparece em Meus pedidos.", "info");
    onCancelar();
  };
  const fechar = () => {
    if (ocupado.current || pagandoMp || saindo) return;
    if (pedidoMp) setConfirmarSaida(true);
    else onCancelar();
  };
  useStoreDialog(dialogRef, true, () => fechar());
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    let atual = true;
    setValidando(true);
    setErro("");
    setCotacao(null);
    setEtapa("pagamento");
    validarCarrinhoLoja(vendedorId, JSON.parse(chaveItens), codigoCupom)
      .then(valor => { if (atual) setCotacao(valor); })
      .catch(error => { if (atual) setErro(mensagemPedidoLoja(error)); })
      .finally(() => { if (atual) setValidando(false); });
    return () => { atual = false; };
  }, [vendedorId, chaveItens, codigoCupom, tentativa]);

  const total = cotacao?.total ?? subtotal;
  const pixConfigurado = !!(cfg.pixEnabled && cfg.pixChave);
  const pixPayload = useMemo(() => {
    if (!pixConfigurado || !cotacao || total <= 0) return "";
    return gerarPixPayload({
      chave: cfg.pixChave,
      nome: cfg.pixBeneficiario || vendedor?.empresa || vendedor?.nome,
      cidade: cfg.pixCidade, valor: total,
      txid: cliente?.uid ? `PED${cliente.uid.slice(-8)}` : "***",
    });
  }, [cfg, total, vendedor, cliente, cotacao, pixConfigurado]);

  useEffect(() => {
    let atual = true;
    setQrUrl("");
    if (etapa === "pix" && pixPayload) {
      QRCode.toDataURL(pixPayload, { width: 240, margin: 1 })
        .then(url => { if (atual) setQrUrl(url); })
        .catch(() => { if (atual) setErro("Não foi possível exibir o QR Code. Use o código PIX para copiar e colar."); });
    }
    return () => { atual = false; };
  }, [etapa, pixPayload]);

  const criarPedido = async (valorConfirmado = cotacao, { finalizar = true, pagamentoOnline } = {}) => {
    const resposta = await enviarPedidoLoja({
      vendedorId, itens, codigo: codigoCupom, metodo,
      requestId: requestId.current, totalConfirmado: valorConfirmado.total, pagamentoOnline,
    });
    if (!mounted.current) return null;
    if (!finalizar) return resposta.pedidoId;
    toast("Pedido enviado! Aguarde a confirmação da loja.", "success");
    onPedidoCriado(resposta.pedidoId);
    return resposta.pedidoId;
  };

  const executar = async acao => {
    if (ocupado.current || validando || !cotacao) return;
    if (!cliente?.uid) { setErro("Entre na sua conta para finalizar o pedido."); return; }
    ocupado.current = true;
    setSalvando(true);
    setErro("");
    try { await acao(); }
    catch (error) {
      if (!mounted.current) return;
      setErro(mensagemPedidoLoja(error));
      // Never let an expired coupon or changed price silently change a PIX already shown.
      if (error?.code?.endsWith("failed-precondition") || error?.code?.endsWith("not-found")) {
        setEtapa("pagamento");
        setCotacao(null);
        setQrUrl("");
      }
    } finally {
      ocupado.current = false;
      if (mounted.current) setSalvando(false);
    }
  };

  const confirmarPagamento = () => executar(async () => {
    const atual = await validarCarrinhoLoja(vendedorId, itens, codigoCupom);
    if (!mounted.current) return;
    setCotacao(atual);
    if (Math.abs(atual.total - cotacao.total) > 0.005) {
      setErro("O valor foi atualizado. Confira o total e confirme novamente.");
      return;
    }
    // Loja com Mercado Pago: o servidor cria o pedido (com o total dele)
    // aguardando o pagamento — invisível para a loja até o servidor confirmar
    // com o Mercado Pago. A cobrança sai da conta da loja.
    if (mpChave && atual.total > 0) {
      const id = await criarPedido(atual, { finalizar: false, pagamentoOnline: "mercadopago" });
      if (id) { setPedidoMp(id); setEtapa("mp"); }
      return;
    }
    if (metodo === "pix" && pixConfigurado && atual.total > 0) setEtapa("pix");
    else await criarPedido(atual);
  });

  const copiarPix = async () => {
    try { await navigator.clipboard.writeText(pixPayload); toast("Código PIX copiado", "success"); }
    catch { toast("Selecione e copie o código PIX manualmente.", "info"); }
  };
  return (
    <div className="store-checkout-overlay" onClick={fechar}>
      <div className="store-checkout-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="store-checkout-title" onClick={e => e.stopPropagation()}>
        <header className="store-checkout-header">
          <h2 id="store-checkout-title">{etapa === "pagamento" ? "Finalizar pedido" : etapa === "mp" ? (metodo === "cartao" ? "Pagar com cartão" : "Pagar com PIX") : "Pagar com PIX"}</h2>
          <button type="button" aria-label="Voltar ao carrinho" disabled={salvando || pagandoMp || saindo} onClick={fechar}>×</button>
        </header>
        <div className="store-cart-summary" aria-live="polite">
          <div><span>Produtos</span><span>{fmt(cotacao?.subtotal ?? subtotal)}</span></div>
          {cotacao?.cupom && <div className="store-cart-discount"><span>Cupom {cotacao.cupom.codigo}</span><span>− {fmt(cotacao.desconto)}</span></div>}
          <div className="store-cart-total"><span>Total</span><strong>{fmt(total)}</strong></div>
        </div>
        {validando && <p className="store-cart-feedback" role="status">Conferindo preços, estoque e desconto…</p>}
        {erro && <p className="store-cart-feedback" role="alert">{erro}</p>}
        {!cotacao && !validando && <button type="button" className="store-checkout-retry" onClick={() => setTentativa(value => value + 1)}>Conferir novamente</button>}

        {etapa === "pagamento" && <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "18px 0" }}>
            <OpcaoPagamento ativo={metodo === "pix"} onClick={() => setMetodo("pix")} disabled={salvando}
              titulo="PIX" desc={mpChave ? "QR Code — confirmação automática" : pixConfigurado ? "QR Code ou copia e cola" : "Combine o pagamento com a loja"} />
            <OpcaoPagamento ativo={metodo === "cartao"} onClick={() => setMetodo("cartao")} disabled={salvando}
              titulo={mpChave ? "Cartão de crédito" : "Cartão"} desc={mpChave ? "Em até 12x, pelo Mercado Pago" : "Disponibilidade a combinar pelo chat"} />
          </div>
          <p className="store-checkout-note">{mpChave ? "A entrega é combinada com a loja. O pagamento é confirmado automaticamente pelo Mercado Pago." : "A entrega é combinada com a loja. O pedido ficará em análise até o vendedor confirmar o pagamento."}</p>
          <BotaoPrim onClick={confirmarPagamento} disabled={salvando || validando || !cotacao}>
            {salvando ? "Conferindo…" : mpChave && total > 0 ? (metodo === "cartao" ? "Continuar para o cartão" : "Gerar PIX") : metodo === "pix" && pixConfigurado && total > 0 ? "Gerar PIX" : "Enviar pedido"}
          </BotaoPrim>
        </>}

        {etapa === "mp" && pedidoMp && <>
          {confirmarSaida && (
            <div role="alertdialog" aria-labelledby="mp-sair-titulo" aria-describedby="mp-sair-texto" style={{ margin: "16px 0", padding: 14, borderRadius: 12, border: "1px solid #f59e0b66", background: "rgba(245,158,11,0.08)" }}>
              <strong id="mp-sair-titulo" style={{ display: "block", fontSize: 15, marginBottom: 6 }}>Sair sem pagar?</strong>
              <p id="mp-sair-texto" className="store-checkout-note" style={{ margin: "0 0 12px" }}>
                O pedido será cancelado e não vai para a loja. Seus itens continuam no carrinho.
                {metodo === "pix" ? " Se você já pagou o PIX, fique nesta tela até a confirmação." : ""}
              </p>
              <div className="store-checkout-actions">
                <button type="button" onClick={sairSemPagar} disabled={saindo} style={btnSec}>{saindo ? "Cancelando…" : "Sair e cancelar"}</button>
                <BotaoPrim onClick={() => setConfirmarSaida(false)} flex disabled={saindo}>Continuar pagando</BotaoPrim>
              </div>
            </div>
          )}
          {/* Continua montado durante a confirmação: voltar a pagar não gera outro PIX nem apaga o cartão digitado */}
          <div hidden={confirmarSaida}>
            <PagamentoMercadoPagoLoja metodo={metodo} vendedorId={vendedorId} pedidoId={pedidoMp} total={total}
              email={cliente?.email || ""} publicKey={mpChave} onPago={pagoMp} onOcupado={setPagandoMp} />
            <button type="button" onClick={fechar} disabled={pagandoMp || saindo} style={{ ...btnSec, width: "100%", marginTop: 10 }}>Cancelar pagamento</button>
          </div>
        </>}

        {etapa === "pix" && <>
          <div className="store-checkout-pix">
            {qrUrl ? <img src={qrUrl} alt="QR Code PIX do pedido" /> : <p className="store-checkout-note">Gerando QR Code…</p>}
            <strong>{fmt(total)}</strong>
            <p className="store-checkout-note">Escaneie ou copie o código abaixo.</p>
          </div>
          <div className="store-checkout-copy">
            <input aria-label="Código PIX copia e cola" readOnly value={pixPayload} onFocus={e => e.target.select()} />
            <button type="button" onClick={copiarPix} style={btnSec}>Copiar</button>
          </div>
          <p className="store-checkout-note">Após pagar, envie a confirmação. Isso não confirma o recebimento automaticamente: a loja irá conferir.</p>
          <div className="store-checkout-actions">
            <button type="button" onClick={() => { setEtapa("pagamento"); setErro(""); }} disabled={salvando} style={btnSec}>Voltar</button>
            <BotaoPrim onClick={() => executar(() => criarPedido())} flex disabled={salvando}>
              {salvando ? "Enviando…" : "Já fiz o pagamento"}
            </BotaoPrim>
          </div>
        </>}
      </div>
    </div>
  );
}

const OpcaoPagamento = ({ ativo, onClick, titulo, desc, disabled }) => (
  <button type="button" aria-pressed={ativo} disabled={disabled} onClick={onClick} style={{
    display: "flex", alignItems: "center", textAlign: "left", padding: "13px 15px", borderRadius: 12, cursor: "pointer",
    background: ativo ? "rgba(0,212,170,0.1)" : "rgba(255,255,255,0.03)",
    border: `1.5px solid ${ativo ? "#00d4aa" : "rgba(255,255,255,0.1)"}`, color: "#f1f5f9",
  }}>
    <span><strong style={{ display: "block", fontSize: 14 }}>{titulo}</strong><span style={{ fontSize: 12, color: "#a4b5c5" }}>{desc}</span></span>
  </button>
);
const BotaoPrim = ({ children, onClick, disabled, flex }) => (
  <button type="button" onClick={onClick} disabled={disabled} style={{
    flex: flex ? 1 : undefined, width: flex ? undefined : "100%", padding: 13, background: "#00d4aa",
    color: "#0a1628", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 14.5,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
  }}>{children}</button>
);
const btnSec = {
  padding: "12px", background: "transparent", color: "#b2c5d5", border: "1px solid #ffffff26",
  borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer",
};
